import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ExceptionResolution } from '@usf/ngrx-order';
import { ImageVariantEnum } from 'src/app/product-detail/models/image-variant.enum';
import { getFirstImageURL } from 'src/app/product-detail/utils/product-images.util';
import { Product } from 'src/app/shared/models/product.model';
import { ResolutionType } from '../../../pages/order-exceptions/constants/resolution-type.enum';
import { EmittedResolution } from '../../../pages/order-exceptions/models/emitted-resolution.model';
import { PlatformEnum } from '@panamax/app-state';
import { Platform } from '@ionic/angular';
import { ExceptionType } from '../../../pages/order-exceptions/constants/order-exception-type.enum';
import { QuantityInputComponent } from 'src/app/shared/components/quantity-input/quantity-input.component';
import { MatLegacyRadioChange as MatRadioChange } from '@angular/material/legacy-radio';
import { ProductSummary } from '@usf/product-types';
import { getAlternativeThumbnail } from 'src/app/product-detail/utils/product-images.util';
import { ApiUpdatable } from 'src/app/shared/models/api-updatable';

@Component({
  template: '',
})
export class UnusualQuantityRowBaseComponent implements OnChanges, OnInit {
  @Input() productState: ProductSummary;
  @Input() exceptionResolution: ExceptionResolution;
  @Input() product: Product;
  @Input() isDisabled: boolean;
  @Output() exceptionResolved = new EventEmitter();
  @Input() exceptionType: ExceptionType;
  @ViewChild('unUsualQtyEachesInput')
  unusualQtyExceptionInputEaches: QuantityInputComponent;
  @ViewChild('unUsualQtyCasesInput')
  unusualQtyExceptionInputCases: QuantityInputComponent;
  isOffline = false; // should always false for Unusual Qty Exception
  thumbnailImageURL: string;
  isInputHidden: boolean;
  isClosed: boolean;
  platformEnum: PlatformEnum;
  suggestedEaches: ApiUpdatable<number>;
  suggestedUnits: ApiUpdatable<number>;
  constructor(readonly platform: Platform) {}

  ngOnInit(): void {
    if (this.platform.is('mobile')) {
      this.platformEnum = PlatformEnum.mobile;
    } else if (this.platform.is('tablet')) {
      this.platformEnum = PlatformEnum.tablet;
    } else {
      this.platformEnum = PlatformEnum.desktop;
    }
    switch (this.exceptionResolution?.orderException.errorCode) {
      case 608:
      case 620:
      case 624:
      case 614: {
        //re-calculated eaches & cases based on case conversion variable
        let calculatedQty = this.exceptionResolutionSuggestedQty(
          this.exceptionResolution?.orderException?.errorCode,
          this.exceptionResolution?.orderItem?.eachesOrdered?.currentValue,
          this.exceptionResolution?.orderItem?.unitsOrdered?.currentValue,
          Number(this.exceptionResolution?.orderException?.exceptionDetails[0]),
        );
        this.suggestedEaches = {
          previousValue: calculatedQty?.newEachesOrdered,
          currentValue: calculatedQty?.newEachesOrdered,
          loading: false,
          error: null,
        };
        this.suggestedUnits = {
          previousValue: calculatedQty?.newUnitsOrdered,
          currentValue: calculatedQty?.newUnitsOrdered,
          loading: false,
          error: null,
        };
        this.isInputHidden = true;
        break;
      }
      case 703:
        this.isInputHidden = true;
        break;
      default:
        this.isInputHidden = true;
        break;
    }
  }

  exceptionResolutionSuggestedQty = (
    errorCode: number,
    eachesOrdered: number = 0,
    unitesOrdered: number = 0,
    messageVariable: number,
  ) => {
    switch (errorCode) {
      case 614:
        const remainingEaches = eachesOrdered % messageVariable;
        const eachesConvertedToCases = eachesOrdered - remainingEaches;
        const newCasesToBeAdded = eachesConvertedToCases / messageVariable;
        return {
          newEachesOrdered: remainingEaches,
          newUnitsOrdered: unitesOrdered + newCasesToBeAdded,
        };
      case 608:
      case 620:
      case 624:
        return { newEachesOrdered: 0, newUnitsOrdered: unitesOrdered };
      default:
        break;
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product?.currentValue) {
      this.thumbnailImageURL = getFirstImageURL(
        changes.product?.currentValue?.summary,
        ImageVariantEnum.Thumbnail,
      );
    }
  }

  edit() {
    const resolution: EmittedResolution = {
      exceptionResolution: { ...this.exceptionResolution, expanded: true },
      resolutionType: ResolutionType.UNUSUAL_QUANTITY,
    };
    this.exceptionResolved.emit(resolution);
  }

  changeQuantityHandler(quantityUpdate: any) {
    if (
      quantityUpdate?.isEaches &&
      this.unusualQtyExceptionInputEaches?.quantity
    ) {
      this.unusualQtyExceptionInputEaches.quantity = {
        ...this.unusualQtyExceptionInputEaches.quantity,
        previousValue: this.unusualQtyExceptionInputEaches.quantity
          .currentValue,
        currentValue: quantityUpdate.quantity,
      };
    } else if (this.unusualQtyExceptionInputCases?.quantity) {
      this.unusualQtyExceptionInputCases.quantity = {
        ...this.unusualQtyExceptionInputCases.quantity,
        previousValue: this.unusualQtyExceptionInputCases.quantity.currentValue,
        currentValue: quantityUpdate.quantity,
      };
    }
  }

  confirmQty() {
    let currentUnits;
    let currentEaches;
    currentUnits = {
      currentValue: Number(
        this.unusualQtyExceptionInputCases.itemInputBox.value,
      ),
      previousValue: Number(
        this.unusualQtyExceptionInputCases.itemInputBox.value,
      ),
      loading: false,
      error: null,
    };
    currentEaches = {
      currentValue: Number(
        this.unusualQtyExceptionInputEaches.itemInputBox.value,
      ),
      previousValue: Number(
        this.unusualQtyExceptionInputEaches.itemInputBox.value,
      ),
      loading: false,
      error: null,
    };
    const cloneExceptionResolution: ExceptionResolution = JSON.parse(
      JSON.stringify(this.exceptionResolution),
    );

    let resolution: EmittedResolution = {
      exceptionResolution: {
        ...this.exceptionResolution,
        resolutionOrderItem: {
          originalProduct: cloneExceptionResolution.orderItem,
          substituteProduct: null,
        },
        expanded: false,
      },
      resolutionType: ResolutionType.UNUSUAL_QUANTITY,
    };
    resolution.exceptionResolution.resolutionOrderItem.originalProduct.eachesOrdered = currentEaches;
    resolution.exceptionResolution.resolutionOrderItem.originalProduct.unitsOrdered = currentUnits;

    this.exceptionResolved.emit(resolution);
  }

  isMsgClosed() {
    this.isClosed = !this.isClosed;
  }

  getAternativeThumbnail = (thumbnailImageURL: string): string =>
    getAlternativeThumbnail(this.productState, thumbnailImageURL);
}
